import { mesi, money } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const PETROL = "#2E5C60";
const MINT = "#8FC0B8";
const TERR = "#E07A5F";

function platformRows(p, name) {
  if (!p || (p.net === 0 && p.gross === 0)) return [];
  if (p.only_net || p.mode === "netto") return [{ label: `${name} netto`, amount: p.net, bold: true }];
  const rows = [];
  if (p.show_gross) rows.push({ label: `${name} — lordo`, amount: p.gross });
  for (const d of p.deductions) if (d.effective && d.show) rows.push({ label: d.label, amount: -d.effective });
  if (p.hidden) rows.push({ label: "Altre trattenute", amount: -p.hidden });
  if (p.show_net) rows.push({ label: `${name} netto`, amount: p.net, bold: true });
  return rows;
}

export default function ReportPreview({ computed: c, meta, history }) {
  const rows = [
    ...platformRows(c.booking, "Booking"),
    ...platformRows(c.airbnb, "Airbnb"),
  ];
  if (c.direct_gross || c.direct_net) {
    if (c.direct_show_gross) rows.push({ label: "Prenotazioni dirette — incasso", amount: c.direct_gross });
    if (c.direct_deductions.considera && c.direct_deductions.show && c.direct_deductions.amount) rows.push({ label: "Costi/trattenute dirette", amount: -c.direct_deductions.amount });
    if (c.direct_show_net) rows.push({ label: "Dirette nette", amount: c.direct_net, bold: true });
  }
  if (c.other_income && c.other_income_show) rows.push({ label: "Altri incassi", amount: c.other_income });

  const months = (history || []).filter((h) => h.year < meta.year || (h.year === meta.year && h.month <= meta.month)).sort((a, b) => a.year - b.year || a.month - b.month);
  const tot = months.reduce((s, h) => ({ ricavi: s.ricavi + h.ricavi, gestione: s.gestione + h.gestione, costi: s.costi + h.costi, netto: s.netto + h.netto }), { ricavi: 0, gestione: 0, costi: 0, netto: 0 });

  const chartData = months.map((h) => ({ label: mesi[h.month].slice(0, 3), netto: h.netto, ricavi: h.ricavi, gestione: h.gestione }));

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden text-slate-800" data-testid="report-preview">
      <div className="p-5 sm:p-6 border-b-4 flex items-start justify-between gap-4" style={{ borderColor: PETROL }}>
        <div>
          <img src="/brand/puravida-logo.png" alt="PuraVida Home" className="rounded-lg" style={{ maxWidth: 132 }} />
        </div>
        <div className="text-right">
          <div className="font-display text-base sm:text-lg font-extrabold" style={{ color: PETROL }}>RENDICONTO PROPRIETARIO</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">PuraVida Control · Property Management</div>
        </div>
      </div>
      <div className="px-5 sm:px-6 pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm">
          <div><span className="text-slate-500">Proprietario:</span> <b>{meta.owner}</b></div>
          <div><span className="text-slate-500">Periodo:</span> <b>{mesi[meta.month]} {meta.year}</b></div>
          <div><span className="text-slate-500">Appartamento:</span> <b>{meta.apartment}</b></div>
          <div><span className="text-slate-500">Struttura:</span> <b>{meta.property || "-"}</b></div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-4">
        {[
          { l: "Ricavi netti", v: c.total_net },
          { l: "Gestione PuraVida Home", v: -c.management_fee },
          { l: "Costi", v: -c.total_costs },
        ].map((b) => (
          <div key={b.l} className="rounded-xl border border-slate-200 p-3 text-center">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{b.l}</div>
            <div className="font-display text-lg font-bold tabular-nums">{money(b.v)}</div>
          </div>
        ))}
        <div className="rounded-xl p-3 text-center text-white" style={{ background: PETROL }}>
          <div className="text-[10px] uppercase tracking-wide text-teal-100">Netto proprietario</div>
          <div className="font-display text-xl font-extrabold tabular-nums">{money(c.owner_result)}</div>
        </div>
      </div>

      <div className="px-6 pb-2">
        <div className="font-display font-bold text-sm mb-1" style={{ color: PETROL }}>Dettaglio ricavi</div>
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className={`py-1.5 ${r.bold ? "font-bold" : ""}`}>{r.label}</td>
                <td className={`py-1.5 text-right tabular-nums ${r.bold ? "font-bold" : ""}`}>{money(r.amount)}</td>
              </tr>
            ))}
            <tr className="border-t-2" style={{ borderColor: PETROL }}>
              <td className="py-2 font-bold">Totale netto ricavi</td>
              <td className="py-2 text-right font-bold tabular-nums">{money(c.total_net)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-6 pb-2 pt-3">
        <div className="font-display font-bold text-sm mb-1" style={{ color: PETROL }}>Dettaglio costi</div>
        <table className="w-full text-sm">
          <tbody>
            {c.shown_cost_rows.filter((r) => r.amount).length === 0 && !c.hidden_costs_total && <tr><td className="py-1.5 text-slate-400">Nessun costo nel periodo</td></tr>}
            {c.shown_cost_rows.filter((r) => r.amount).map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1.5">{r.label}</td>
                <td className="py-1.5 text-right tabular-nums">{money(r.amount)}</td>
              </tr>
            ))}
            {c.hidden_costs_total > 0 && (
              <tr className="border-b border-slate-100"><td className="py-1.5">Altri costi complessivi</td><td className="py-1.5 text-right tabular-nums">{money(c.hidden_costs_total)}</td></tr>
            )}
            <tr className="border-t-2" style={{ borderColor: PETROL }}>
              <td className="py-2 font-bold">Totale costi</td>
              <td className="py-2 text-right font-bold tabular-nums">{money(c.total_costs)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-6 pb-4 pt-3">
        <div className="font-display font-bold text-sm mb-1" style={{ color: PETROL }}>Calcolo finale</div>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-slate-100"><td className="py-1.5">Totale netto ricavi</td><td className="py-1.5 text-right tabular-nums">{money(c.total_net)}</td></tr>
            <tr className="border-b border-slate-100"><td className="py-1.5">Gestione PuraVida Home ({c.management_percent}%)</td><td className="py-1.5 text-right tabular-nums">- {money(c.management_fee)}</td></tr>
            <tr className="border-b border-slate-100"><td className="py-1.5">Costi</td><td className="py-1.5 text-right tabular-nums">- {money(c.total_costs)}</td></tr>
          </tbody>
        </table>
        <div className="mt-2 rounded-xl p-3 flex justify-between items-center text-white" style={{ background: PETROL }}>
          <span className="font-bold">NETTO PROPRIETARIO</span>
          <span className="font-display text-2xl font-extrabold tabular-nums">{money(c.owner_result)}</span>
        </div>
      </div>

      {meta.notes && (
        <div className="px-6 pb-6 pt-2">
          <div className="font-display font-bold text-sm mb-1" style={{ color: PETROL }}>Note del mese</div>
          <div className="text-sm text-slate-600 whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-3">{meta.notes}</div>
        </div>
      )}

      {months.length > 0 && (
        <div className="px-6 pb-6 pt-2">
          <div className="font-display font-bold text-sm mb-1" style={{ color: PETROL }}>Andamento anno {meta.year}</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 uppercase border-b border-slate-200"><th className="py-1">Mese</th><th className="text-right">Ricavi netti</th><th className="text-right">Gestione</th><th className="text-right">Costi</th><th className="text-right">Netto proprietario</th></tr></thead>
            <tbody>
              {months.map((h) => (
                <tr key={`${h.year}-${h.month}`} className="border-b border-slate-100">
                  <td className="py-1">{mesi[h.month]}</td>
                  <td className="text-right tabular-nums">{money(h.ricavi)}</td>
                  <td className="text-right tabular-nums">{money(h.gestione)}</td>
                  <td className="text-right tabular-nums">{money(h.costi)}</td>
                  <td className="text-right tabular-nums font-semibold">{money(h.netto)}</td>
                </tr>
              ))}
              <tr className="font-bold border-t-2" style={{ borderColor: PETROL }}>
                <td className="py-1.5">TOTALE ANNO</td>
                <td className="text-right tabular-nums">{money(tot.ricavi)}</td>
                <td className="text-right tabular-nums">{money(tot.gestione)}</td>
                <td className="text-right tabular-nums">{money(tot.costi)}</td>
                <td className="text-right tabular-nums">{money(tot.netto)}</td>
              </tr>
            </tbody>
          </table>
          {months.length > 1 && (
            <div className="mt-4" data-testid="preview-chart">
              <div className="text-[11px] text-slate-500 mb-1">Valori in euro</div>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer minWidth={0} minHeight={260} debounce={50}>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: -14, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EDF3F3" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickMargin={8} />
                    <YAxis tick={{ fontSize: 10, fill: "#64748B" }} width={52}
                      tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="netto" name="Netto proprietario" stroke={PETROL} strokeWidth={3} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="ricavi" name="Ricavi netti" stroke={MINT} strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="gestione" name="Gestione PuraVida" stroke={TERR} strokeWidth={1.8} dot={{ r: 2 }} strokeDasharray="4 3" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
